/**
 * Order Limits Validator - Theme-side validation
 *
 * Provides immediate user feedback for quantity limits before checkout.
 * This is a client-side validation that mirrors the server-side Shopify Function.
 *
 * Note: This is for UX only. The Shopify Function provides the actual enforcement.
 */

class OrderLimitsValidator {
  constructor(config) {
    this.config = config || { enabled: true, products: [], vendors: [] };
    this.init();
  }

  init() {
    if (!this.config.enabled) {
      return;
    }

    // Listen for cart updates
    document.addEventListener('cart:updated', () => this.validateCart());

    // Validate on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.validateCart());
    } else {
      this.validateCart();
    }

    // Listen for quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="quantity"], [name*="[quantity]"]')) {
        setTimeout(() => this.validateCart(), 100);
      }
    });
  }

  /**
   * Fetch current cart from Shopify
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return null;
    }
  }

  /**
   * Extract numeric ID from product ID
   */
  extractNumericId(id) {
    return String(id).split('/').pop();
  }

  /**
   * Check if product ID matches config ID
   */
  productIdMatches(configId, productId) {
    const configNumeric = String(configId).split('/').pop();
    const productNumeric = this.extractNumericId(productId);
    return configNumeric === productNumeric;
  }

  /**
   * Validate cart against configured limits
   */
  async validateCart() {
    const cart = await this.getCart();
    if (!cart || !cart.items) {
      return;
    }

    const errors = [];
    const warnings = [];

    // Validate product-specific limits
    for (const item of cart.items) {
      const productId = item.product_id;
      const productTitle = item.product_title;
      const quantity = item.quantity;

      // Check product-specific limits
      const productLimit = this.config.products.find(p =>
        this.productIdMatches(p.productId, productId)
      );

      if (productLimit) {
        if (productLimit.maxQuantity && quantity > productLimit.maxQuantity) {
          errors.push({
            type: 'product_max',
            message: `Maximum quantity for "${productTitle}" is ${productLimit.maxQuantity}. You have ${quantity} in your cart.`,
            productId: productId,
            variantId: item.variant_id,
            limit: productLimit.maxQuantity,
            current: quantity
          });
        }

        if (productLimit.minQuantity && quantity < productLimit.minQuantity) {
          warnings.push({
            type: 'product_min',
            message: `Minimum quantity for "${productTitle}" is ${productLimit.minQuantity}. You have ${quantity} in your cart.`,
            productId: productId,
            variantId: item.variant_id,
            limit: productLimit.minQuantity,
            current: quantity
          });
        }
      }
    }

    // Validate vendor-specific limits
    const vendorQuantities = new Map();

    for (const item of cart.items) {
      const vendor = item.vendor;
      const currentQty = vendorQuantities.get(vendor) || 0;
      vendorQuantities.set(vendor, currentQty + item.quantity);
    }

    for (const [vendor, totalQuantity] of vendorQuantities.entries()) {
      const vendorLimit = this.config.vendors.find(v =>
        v.vendor.toLowerCase() === vendor.toLowerCase()
      );

      if (vendorLimit) {
        if (vendorLimit.maxQuantity && totalQuantity > vendorLimit.maxQuantity) {
          errors.push({
            type: 'vendor_max',
            message: `Maximum total quantity for ${vendor} products is ${vendorLimit.maxQuantity}. You have ${totalQuantity} items from ${vendor} in your cart.`,
            vendor: vendor,
            limit: vendorLimit.maxQuantity,
            current: totalQuantity
          });
        }

        if (vendorLimit.minQuantity && totalQuantity < vendorLimit.minQuantity) {
          warnings.push({
            type: 'vendor_min',
            message: `Minimum total quantity for ${vendor} products is ${vendorLimit.minQuantity}. You have ${totalQuantity} items from ${vendor} in your cart.`,
            vendor: vendor,
            limit: vendorLimit.minQuantity,
            current: totalQuantity
          });
        }
      }
    }

    // Display errors and warnings
    this.displayValidation(errors, warnings);

    // Disable checkout button if there are errors
    this.toggleCheckoutButton(errors.length === 0);

    // Dispatch custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('order-limits:validated', {
      detail: { errors, warnings, valid: errors.length === 0 }
    }));

    return { errors, warnings };
  }

  /**
   * Display validation messages in the cart
   */
  displayValidation(errors, warnings) {
    // Remove any existing validation messages
    const existingMessages = document.querySelectorAll('.order-limits-message');
    existingMessages.forEach(el => el.remove());

    const cartForm = document.querySelector('form[action="/cart"]') ||
                     document.querySelector('.cart-form') ||
                     document.querySelector('[data-cart-form]');

    /* DEBUG
    if (!cartForm) {
      console.warn('Cart form not found for displaying validation messages');
      return;
    }
    */

    // Create container for messages
    const container = document.createElement('div');
    container.className = 'order-limits-messages';

    // Add error messages
    errors.forEach(error => {
      const messageEl = document.createElement('div');
      messageEl.className = 'order-limits-message order-limits-error';
      messageEl.setAttribute('role', 'alert');
      messageEl.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8 13C5.24 13 3 10.76 3 8C3 5.24 5.24 3 8 3C10.76 3 13 5.24 13 8C13 10.76 10.76 13 8 13ZM8 7H7.5C7.22 7 7 7.22 7 7.5V11.5C7 11.78 7.22 12 7.5 12H8.5C8.78 12 9 11.78 9 11.5V7.5C9 7.22 8.78 7 8.5 7H8ZM8 6C8.55 6 9 5.55 9 5C9 4.45 8.55 4 8 4C7.45 4 7 4.45 7 5C7 5.55 7.45 6 8 6Z" fill="currentColor"/>
        </svg>
        <span>${error.message}</span>
      `;
      container.appendChild(messageEl);
    });

    // Add warning messages
    warnings.forEach(warning => {
      const messageEl = document.createElement('div');
      messageEl.className = 'order-limits-message order-limits-warning';
      messageEl.setAttribute('role', 'status');
      messageEl.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 1.5L1.5 13.5H14.5L8 1.5ZM8.5 11.5H7.5V10.5H8.5V11.5ZM8.5 9.5H7.5V6.5H8.5V9.5Z" fill="currentColor"/>
        </svg>
        <span>${warning.message}</span>
      `;
      container.appendChild(messageEl);
    });

    // Insert messages into cart
    if (container.children.length > 0) {
      const checkoutButton = cartForm.querySelector('[name="checkout"]') ||
                             cartForm.querySelector('button[type="submit"]') ||
                             cartForm.querySelector('.cart__checkout');

      if (checkoutButton) {
        checkoutButton.parentNode.insertBefore(container, checkoutButton);
      } else {
        cartForm.insertBefore(container, cartForm.firstChild);
      }
    }
  }

  /**
   * Enable or disable checkout button
   */
  toggleCheckoutButton(enable) {
    const checkoutButtons = document.querySelectorAll(
      '[name="checkout"], .cart__checkout, [data-cart-checkout]'
    );

    checkoutButtons.forEach(button => {
      if (enable) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.classList.remove('disabled');
      } else {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('disabled');
      }
    });
  }
}

// Initialize when DOM is ready
if (typeof window !== 'undefined') {
  // Configuration can be injected via Liquid template
  // window.orderLimitsConfig should be set in your theme's liquid file
  const initValidator = () => {
    if (window.orderLimitsConfig) {
      window.orderLimitsValidator = new OrderLimitsValidator(window.orderLimitsConfig);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initValidator);
  } else {
    initValidator();
  }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = OrderLimitsValidator;
}
